/**
 * Full-screen wrapper around RiveComponent for one stage animation file.
 *
 * Belongs here: playback lifecycle (onStop, timeout fallback), layout padding.
 * Does not belong here: which .riv to show or when to advance the questionnaire.
 */
import { useCallback, useEffect, useRef } from 'react'
import RiveComponent, { RiveFit } from '../../../../../components/RiveComponent'
import { getBottomPadding } from '../../../../../utils/sessionStorage'

export interface StageRiveWrapperProps {
  onComplete: () => void
  timeoutMs: number
  riveSrc: string
}

export function StageRiveWrapper({ onComplete, timeoutMs, riveSrc }: StageRiveWrapperProps) {
  const completedRef = useRef(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  const finishRivePlayback = useCallback(() => {
    if (completedRef.current) return
    completedRef.current = true
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    onCompleteRef.current()
  }, [])

  useEffect(() => {
    completedRef.current = false
    // Safety net if Rive never fires onStop (load error, zero-length animation)
    timeoutRef.current = setTimeout(finishRivePlayback, timeoutMs)
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [timeoutMs, riveSrc, finishRivePlayback])

  return (
    <div
      className='flex w-full flex-1 min-h-0 flex-col items-center justify-center bg-white'
      style={{ paddingBottom: `${getBottomPadding() + 24}px` }}
      aria-hidden
    >
      <div className='w-full h-full min-h-[240px]'>
        <RiveComponent
          key={riveSrc}
          src={riveSrc}
          fit={RiveFit.WIDTH}
          autoplay
          onStop={finishRivePlayback}
          fallback={
            <div
              className='w-full h-full min-h-[240px] rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 text-4xl'
              aria-hidden
            >
              ✓
            </div>
          }
        />
      </div>
    </div>
  )
}
